import { useEffect, useState } from "react";
import { FormattedMessage, FormattedNumber, useIntl } from "react-intl";
import { Link, useNavigate } from "react-router-dom";
import { endpoints } from "../api.js";
import { PURPOSES } from "../config.js";
import { Icon } from "../components/Icon.jsx";
import { Banner, Credits, ErrorBanner, Loading, Spinner } from "../components/ui.jsx";
import { useDocumentTitle, useErrorMessage, useLocalePath } from "../i18n/hooks.js";
import { useSession } from "../session.jsx";
import { useWorkspace } from "../workspace.jsx";

const TYPE_ICON = { domain: "globe", ip: "target", email: "file", username: "users", phone: "note", person: "users" };

export default function NewInvestigation() {
  useDocumentTitle("newInv.title");
  const intl = useIntl();
  const lp = useLocalePath();
  const navigate = useNavigate();
  const errMsg = useErrorMessage();
  const { tenant, refreshTenant } = useSession();
  const { trackJob } = useWorkspace();

  const [types, setTypes] = useState(null);
  const [typesError, setTypesError] = useState(null);
  const [form, setForm] = useState({
    name: "",
    targetType: "",
    target: "",
    purpose: "",
    purposeText: "",
    legalBasis: "",
  });
  const [estimate, setEstimate] = useState(null);
  const [estimating, setEstimating] = useState(false);
  const [estimateError, setEstimateError] = useState(null);
  const [step, setStep] = useState("form"); // form | confirm
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [createdInv, setCreatedInv] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    endpoints.targetTypes().then((d) => {
      const list = d.types || [];
      setTypes(list);
      const first = list.find((t) => t.enabled);
      if (first) setForm((f) => (f.targetType ? f : { ...f, targetType: first.id }));
    }, setTypesError);
  }, []);

  // Live cost estimate for the chosen target type.
  useEffect(() => {
    if (!form.targetType) return;
    let alive = true;
    setEstimating(true);
    setEstimateError(null);
    endpoints
      .estimate(form.targetType)
      .then((d) => alive && setEstimate(d))
      .catch((e) => alive && (setEstimate(null), setEstimateError(e)))
      .finally(() => alive && setEstimating(false));
    return () => {
      alive = false;
    };
  }, [form.targetType]);

  const selectedType = types?.find((t) => t.id === form.targetType);
  const isTierB = selectedType?.tier === "B";
  const balance = tenant?.creditBalance ?? 0;
  const insufficient = estimate && estimate.estimatedCredits > balance;
  const valid =
    form.name.trim() &&
    form.target.trim() &&
    form.targetType &&
    selectedType?.enabled &&
    form.purpose &&
    form.purposeText.trim() &&
    (!isTierB || form.legalBasis.trim());

  const startJob = async (inv) => {
    const { job } = await endpoints.startJob(inv.id);
    trackJob(job, inv);
    refreshTenant();
    navigate(lp(`/jobs/${job.id}`));
  };

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      let inv = createdInv;
      if (!inv) {
        const body = {
          name: form.name.trim(),
          targetType: form.targetType,
          target: form.target.trim(),
          purpose: form.purpose,
          purposeText: form.purposeText.trim(),
        };
        if (isTierB) body.legalBasis = form.legalBasis.trim();
        ({ investigation: inv } = await endpoints.createInvestigation(body));
        setCreatedInv(inv);
      }
      await startJob(inv);
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  };

  if (typesError) return <div className="page"><ErrorBanner message={errMsg(typesError)} /></div>;

  return (
    <div className="page page-narrow">
      <header className="page-head">
        <div>
          <Link to={lp("/investigations")} className="back-link">
            <Icon name="arrowLeft" size={15} />
            <FormattedMessage id="nav.investigations" />
          </Link>
          <h1 className="h1">
            <FormattedMessage id="newInv.title" />
          </h1>
          <p className="muted">
            <FormattedMessage id="newInv.subtitle" />
          </p>
        </div>
      </header>

      {tenant && tenant.verificationLevel < 1 ? (
        <Banner
          kind="warn"
          icon="lock"
          action={
            <Link className="btn btn-sm btn-outline" to={lp("/onboarding")}>
              <FormattedMessage id="newInv.completeSetup" />
            </Link>
          }
        >
          <FormattedMessage id="newInv.needL1" />
        </Banner>
      ) : null}

      <div className="new-inv">
        <form
          className="card form"
          onSubmit={(e) => {
            e.preventDefault();
            if (valid) setStep("confirm");
          }}
        >
          <fieldset className="fieldset" disabled={step === "confirm"}>
            <legend className="field-label">
              <FormattedMessage id="newInv.targetType" />
            </legend>
            {!types ? <Loading /> : null}
            <div className="type-grid">
              {types?.map((t) => (
                <label
                  key={t.id}
                  className={`type-card ${form.targetType === t.id ? "is-selected" : ""} ${t.enabled ? "" : "is-locked"}`}
                  title={t.enabled ? undefined : intl.formatMessage({ id: "newInv.requiresL2" })}
                >
                  <input
                    type="radio"
                    name="targetType"
                    value={t.id}
                    checked={form.targetType === t.id}
                    disabled={!t.enabled}
                    onChange={set("targetType")}
                  />
                  <Icon name={t.enabled ? TYPE_ICON[t.id] || "target" : "lock"} size={20} />
                  <span className="type-name">
                    <FormattedMessage id={`targetType.${t.id}`} defaultMessage={t.id} />
                  </span>
                  <span className="type-tier">
                    <FormattedMessage id="newInv.tier" values={{ tier: t.tier }} />
                  </span>
                  {!t.enabled ? (
                    <span className="type-hint">
                      <FormattedMessage id="newInv.requiresL2" />
                    </span>
                  ) : null}
                </label>
              ))}
            </div>
          </fieldset>

          <fieldset className="fieldset" disabled={step === "confirm"}>
            <label className="field">
              <span className="field-label">
                <FormattedMessage id="newInv.target" />
              </span>
              <input
                required
                className="mono"
                value={form.target}
                onChange={set("target")}
                placeholder={intl.formatMessage({
                  id: `newInv.targetPlaceholder.${form.targetType || "domain"}`,
                  defaultMessage: "",
                })}
                autoComplete="off"
                spellCheck={false}
              />
            </label>
            <label className="field">
              <span className="field-label">
                <FormattedMessage id="newInv.name" />
              </span>
              <input required maxLength={200} value={form.name} onChange={set("name")} />
            </label>
            <label className="field">
              <span className="field-label">
                <FormattedMessage id="newInv.purpose" />
              </span>
              <select required value={form.purpose} onChange={set("purpose")}>
                <option value="" disabled>
                  {intl.formatMessage({ id: "newInv.purposePlaceholder" })}
                </option>
                {PURPOSES.map((p) => (
                  <option key={p} value={p}>
                    {intl.formatMessage({ id: `purpose.${p}` })}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span className="field-label">
                <FormattedMessage id="newInv.purposeText" />
              </span>
              <textarea
                required
                rows={3}
                maxLength={2000}
                value={form.purposeText}
                onChange={set("purposeText")}
                placeholder={intl.formatMessage({ id: "newInv.purposeTextPlaceholder" })}
              />
              <span className="field-help">
                <FormattedMessage id="newInv.purposeHelp" />
              </span>
            </label>
            {isTierB ? (
              <label className="field">
                <span className="field-label">
                  <FormattedMessage id="newInv.legalBasis" />
                </span>
                <textarea rows={2} value={form.legalBasis} onChange={set("legalBasis")} />
              </label>
            ) : null}
          </fieldset>

          <ErrorBanner message={error ? errMsg(error) : null} />
          {error?.code === "insufficient_credits" ? (
            <Banner kind="warn" icon="coins" action={
              <Link className="btn btn-sm btn-outline" to={lp("/settings#billing")}>
                <FormattedMessage id="newInv.topUp" />
              </Link>
            }>
              <FormattedMessage id="newInv.insufficientHelp" />
            </Banner>
          ) : null}
          {createdInv && error ? (
            <p className="small muted">
              <FormattedMessage
                id="newInv.createdButNotStarted"
                values={{ link: (c) => <Link to={lp(`/investigations/${createdInv.id}`)}>{c}</Link> }}
              />
            </p>
          ) : null}

          <div className="btn-row">
            {step === "form" ? (
              <button type="submit" className="btn btn-primary" disabled={!valid || estimating}>
                <FormattedMessage id="newInv.review" />
                <Icon name="arrowRight" size={16} />
              </button>
            ) : (
              <>
                <button type="button" className="btn btn-primary" onClick={confirm} disabled={busy}>
                  {busy ? <Spinner size={16} /> : <Icon name="play" size={15} />}
                  <FormattedMessage
                    id="newInv.confirm"
                    values={{ credits: estimate?.estimatedCredits ?? 0 }}
                  />
                </button>
                <button
                  type="button"
                  className="btn btn-ghost"
                  disabled={busy}
                  onClick={() => {
                    setStep("form");
                    setError(null);
                  }}
                >
                  <FormattedMessage id="common.back" />
                </button>
              </>
            )}
          </div>
          {step === "confirm" ? (
            <p className="small muted">
              <FormattedMessage id="newInv.confirmNote" />
            </p>
          ) : null}
        </form>

        <aside className="card estimate-card">
          <div className="stat-label">
            <Icon name="coins" size={16} />
            <FormattedMessage id="newInv.estimate" />
          </div>
          {estimating ? <Loading /> : null}
          <ErrorBanner message={estimateError ? errMsg(estimateError) : null} />
          {estimate && !estimating ? (
            <>
              <div className="estimate-total">
                <span className="stat-value grad-text">
                  <FormattedNumber value={estimate.estimatedCredits} />
                </span>
                <span className="muted small">
                  <FormattedMessage id="newInv.creditsMax" />
                </span>
              </div>
              <ul className="source-list">
                {(estimate.sources || []).map((s) => (
                  <li key={s.id}>
                    <span className="truncate">{s.name}</span>
                    <span className="mono muted">
                      <FormattedNumber value={s.creditCost} />
                    </span>
                  </li>
                ))}
              </ul>
              <div className={`balance-line ${insufficient ? "is-bad" : ""}`}>
                <FormattedMessage id="newInv.balance" />
                <strong>
                  <Credits value={balance} />
                </strong>
              </div>
              {insufficient ? (
                <p className="small bad-text">
                  <FormattedMessage id="error.insufficient_credits" />
                </p>
              ) : null}
              <p className="small muted">
                <FormattedMessage id="newInv.refundNote" />
              </p>
            </>
          ) : null}
        </aside>
      </div>
    </div>
  );
}
